import { useEffect, useRef, useState } from "react";
import moment from "moment";
import { FaCircleInfo } from "react-icons/fa6";
import Swal from "sweetalert2";
import { useMutation, useQuery } from "@tanstack/react-query";

import * as images from "../../assets/image";
import useDetails from "../../hooks/useDetails";
import { acceptEvent, getOpenEvents } from "../../services/services";
import { constant } from "../../utils/constants";
import Pagination from "../../utils/Pagination";
import { toastAlert } from "../../utils/SweetAlert";

const Open = () => {
  const details = useDetails();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(constant.PAGE_NO_ONE);
  const tableContainerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

const { data, refetch } = useQuery({
  queryKey: ["open-assignments", page],
  queryFn: async () => {
    let params = {
      page: page,
      limit: constant.PAGE_LIMIT,
      sortedBy: constant.SORTED_BY.START_TIME,
      sortBy: constant.SORT_BY.ASC,
    };
    if (search) params.search = search;
    const resp = await getOpenEvents(params);
    const events = resp?.data?.data?.events || [];
  
    return {
      events: events,
      total_pages: Math.ceil(
        (resp?.data?.data?.total_count || 0) / constant.PAGE_LIMIT
      ),
    };
  },
});
  const acceptMutation = useMutation({
    mutationFn: async (event) => {
      try {
        const response = await acceptEvent({
          id: event._id,
          accept: true,
        });
        console.log("Accept Success:", response);
        return response;
      } catch (error) {
        error.event = event;
        console.error("Accept Mutation Error:", error);
        throw error;
      }
    },
    onSuccess: (resp) => {
      toastAlert("success", "Assignment accepted successfully");
      refetch();
    },
    onError: (error) => {
      if (error.response?.data?.message === "You have another event assigned") {
        Swal.fire({
          title: "Cannot Accept Assignment",
          text: "There is a scheduling conflict for this assignment, Please contact your manager to accept this assignment",
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: "#d33",
        });
      } else {
        Swal.fire({
          title: error.conflictEvent ? "Time Conflict Detected" : "Cannot Accept Assignment",
          html: error.conflictEvent
            ? `
                <p>This assignment conflicts with your existing schedule:</p>
                <p><strong>${moment(error.conflictEvent.startTime).format("MMM D, YYYY h:mm A")}</strong> to 
                <strong>${moment(error.conflictEvent.endTime).format("MMM D, YYYY h:mm A")}</strong></p>
                <p>Would you like to deny this assignment instead?</p>
              `
            : `
                <p>Could not accept assignment due to:</p>
                <p><strong>${error.response?.data?.message || error.message || "Unknown error"}</strong></p>
                <p>Would you like to deny this assignment instead?</p>
              `,
          icon: "error",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#6c757d",
          confirmButtonText: "Deny Assignment",
          cancelButtonText: "Cancel",
        }).then((result) => {
          if (result.isConfirmed && error.event) {
            denyMutation.mutate(error.event);
          }
        });
      }
    },
  });

  const denyMutation = useMutation({
    mutationFn: async (event) => {
      await acceptEvent({
        id: event._id,
        accept: false,
      });
    },
    onSuccess: () => {
      toastAlert("success", "Assignment denied successfully");
      refetch();
    },
  });

  const handleAcceptClick = (event) => {
    Swal.fire({
      title: "Confirm Acceptance",
      text: "Are you sure you want to accept this assignment?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Accept",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        acceptMutation.mutate(event);
      }
    });
  };

  const handleClientNote = (clientDetails) => {
    if (!clientDetails?.note?.trim()) return;

    Swal.fire({
      title: `${clientDetails?.firstName || "Client"}'s Note`,
      text: clientDetails.note,
      icon: "info",
      confirmButtonText: "Okay",
      confirmButtonColor: "#3085d6",
    });
  };

  return (
    <section className="main-content schedules_bar">
      <div className="commonSearchBar mt-3">
        <h4 className="heading20">Open Assignments</h4>
        <div className="clientSearch">
          <img
            src={images.Search}
            alt="searchImg"
            className="clientSearchIcon"
          />
          <input
            type="text"
            placeholder="Type and press enter to search."
            className="clientSearchInput"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && search.trim() !== "") {
                refetch();
              }
            }}
            onKeyUp={(e) => {
              if (e.target.value === "" && e.key === "Backspace") {
                setSearch("");
                refetch();
              }
            }}
          />
        </div>
      </div>

      <div className="container-fluid p-0">
        <div className="row mt-4">
          <div className="col-md-12">
            <div
              ref={tableContainerRef}
              style={{
                overflowX: isMobile ? "auto" : "visible",
                WebkitOverflowScrolling: "touch",
                width: "100%",
                position: "relative",
                paddingBottom: isMobile ? "20px" : "0",
              }}
            >
              {isMobile && (
                <div
                  style={{
                    position: "absolute",
                    bottom: "0",
                    left: "50%",
                    transform: "translateX(-50%)",
                    color: "#666",
                    fontSize: "12px",
                  }}
                >
                  ← Scroll →
                </div>
              )}

              <table
                style={{
                  width: "100%",
                  minWidth: isMobile ? "800px" : "100%",
                  borderCollapse: "separate",
                  borderSpacing: "0",
                }}
              >
                <thead style={{ backgroundColor: "#f8f9fa" }}>
                  <tr>
                    <th className="tableHeading">Date</th>
                    <th className="tableHeading">Time</th>
                    <th
                      className="tableHeading"
                      style={{
                        position: isMobile ? "sticky" : "static",
                        left: isMobile ? "0" : "auto",
                        zIndex: isMobile ? "2" : "auto",
                        boxShadow: isMobile
                          ? "2px 0 5px -2px rgba(0,0,0,0.1)"
                          : "none",
                        minWidth: "120px",
                      }}
                    >
                      Client Name
                    </th>
                    <th className="tableHeading" style={{ minWidth: isMobile ? "120px" : "150px" }}>
                      Address
                    </th>
                    <th className="tableHeading" style={{ minWidth: isMobile ? "80px" : "100px" }}>
                      Phone
                    </th>
                    <th className="tableHeading" style={{ display: isMobile ? "none" : "table-cell" }}>
                      Email
                    </th>
                    <th className="tableHeading" style={{ minWidth: "80px" }}>Accept</th>
                  </tr>
                </thead>
                <tbody>
                  {data && data?.events && data?.events?.length > 0 ? (
                    data.events.map((event, index) => {
                      const clientDetails = event?.clientDetails || {};
                      return (
                        <tr key={`event_${index}`}>
                          <td>
                            {moment(event?.startTime).format(
                              "ddd, MMM DD, YYYY"
                            )}
                          </td>
                          <td>
                            {moment(event?.startTime).format("LT")} -{" "}
                            {moment(event?.endTime).format("LT")}
                          </td>
                          <td
                            style={{
                              position: isMobile ? "sticky" : "static",
                              left: isMobile ? "0" : "auto",
                              backgroundColor: isMobile ? "white" : "transparent",
                              zIndex: isMobile ? "2" : "auto",
                              boxShadow: isMobile
                                ? "2px 0 5px -2px rgba(0,0,0,0.1)"
                                : "none",
                              minWidth: "120px",
                            }}
                            className="text-capitalize"
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                                gap: "8px",
                              }}
                            >
                              <span style={{ flex: 1, minWidth: 0 }}>{`${clientDetails?.firstName || ""} ${clientDetails?.lastName || ""}`}</span>
                              {clientDetails?.note?.trim() && (
                                <button
                                  type="button"
                                  title="View note"
                                  aria-label="View client note"
                                  onClick={() => handleClientNote(clientDetails)}
                                  style={{
                                    border: "none",
                                    background: "transparent",
                                    padding: 0,
                                    margin: 0,
                                    color: "#0d1227",
                                    cursor: "pointer",
                                    lineHeight: 0,
                                    flexShrink: 0,
                                  }}
                                >
                                  <FaCircleInfo size={16} />
                                </button>
                              )}
                            </div>
                          </td>
                          <td>
                            {`${clientDetails?.userAddress?.address || ""} ${
                              clientDetails?.userAddress?.city || ""
                            } ${clientDetails?.userAddress?.state || ""} ${
                              clientDetails?.userAddress?.country || ""
                            } ${clientDetails?.userAddress?.zipCode ? `(${clientDetails.userAddress.zipCode})` : ""}`}
                          </td>
                          <td>{clientDetails?.phoneNumber || "--"}</td>
                          <td style={{ display: isMobile ? "none" : "table-cell" }}>
                            {clientDetails?.email || "--"}
                          </td>
                          <td>
                            <button
                              style={{
                                backgroundColor: "#0d1227",
                                color: "white",
                                border: "none",
                                padding: "5px 10px",
                                borderRadius: "4px",
                              }}
                              onClick={() => handleAcceptClick(event)}
                              disabled={acceptMutation.isLoading || denyMutation.isLoading}
                            >
                              {acceptMutation.isLoading ? "Processing..." : "Accept"}
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td style={{ textAlign: "center" }} colSpan={7}>
                        No Open Assignments Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={page}
              totalPages={data?.total_pages || 1}
              setPage={setPage}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Open;
